import { getReportsFindAllQueryKey } from '@/api/WhisApi/generated';
import { showToast } from '@/components/Toast/toast.service';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { useCallback } from 'react';

interface UseReportSubmissionNavigationOptions {
  isBoardRoute: boolean;
  isEditContext: boolean;
  editedReportId?: string;
  onBeforeNavigate?: () => void;
}

interface UseReportSubmissionNavigationResult {
  handleSubmitError: () => void;
  handleSubmissionSuccess: (
    mode: 'draft' | 'published',
    latestReportId?: string,
  ) => void;
  handleQueuedSuccess: () => void;
}

export const useReportSubmissionNavigation = ({
  isBoardRoute,
  isEditContext,
  editedReportId,
  onBeforeNavigate,
}: UseReportSubmissionNavigationOptions): UseReportSubmissionNavigationResult => {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const router = useRouter();

  const reportDetailPathname = isBoardRoute
    ? '/(tabs)/board/reports/[reportId]'
    : '/(tabs)/reports/[reportId]';
  const reportsListPathname = isBoardRoute
    ? '/(tabs)/board'
    : '/(tabs)/reports';

  const handleSubmitError = useCallback((): void => {
    showToast({
      type: 'error',
      title: t('reportCreation.toasts.submitError'),
    });
  }, [t]);

  const handleSubmissionSuccess = useCallback(
    (mode: 'draft' | 'published', latestReportId?: string): void => {
      onBeforeNavigate?.();
      queryClient.invalidateQueries({ queryKey: getReportsFindAllQueryKey() });
      const targetReportId = latestReportId ?? editedReportId;

      if (isEditContext && targetReportId) {
        router.dismissTo(reportsListPathname);
        router.push({
          pathname: reportDetailPathname,
          params: { reportId: targetReportId },
        });
        return;
      }

      router.replace(
        mode === 'draft'
          ? `/reports/${targetReportId}`
          : '/report-creation/success',
      );
    },
    [
      editedReportId,
      isEditContext,
      onBeforeNavigate,
      queryClient,
      reportsListPathname,
      reportDetailPathname,
      router,
    ],
  );

  const handleQueuedSuccess = useCallback((): void => {
    onBeforeNavigate?.();
    router.replace('/report-creation/success?status=queued');
  }, [onBeforeNavigate, router]);

  return {
    handleSubmitError,
    handleSubmissionSuccess,
    handleQueuedSuccess,
  };
};
